<template>
  <div class="research-center">
    <div class="research-center-content">
      <research-filter-bar
        :assessmentList="assessmentList"
        v-model="filterData"
        @onExportClick="onExportClick"
        @onSearchClick="onSearchClick"
        @onFilterChange="onFilterChange"
      />
      <div class="content">
        <div class="content-main">
          <page-list-container
            class="content-main-table"
            :class="{ 'has-horizontal-scroll': selectedAssessments.length > 2 }"
            :total="total"
            :pageSize="pageSize"
            :inputCurrentPage="currentPage"
            @onPageChange="onPageChange"
            @onSizeChange="onSizeChange"
          >
            <el-table
              class="research-table"
              :key="tableKey"
              :data="tableData"
              row-key="uniqueRowKey"
              height="100%"
              :row-style="{ height: '54px' }"
              :cell-style="{ height: '54px', padding: '0' }"
              :style="{ width: selectedAssessments.length > 2 ? 'max-content' : '100%', minWidth: '100%' }"
            >
              <!-- 用户信息组 -->
              <el-table-column align="center" :width="userInfoGroupWidth" :min-width="userInfoGroupWidth"  label="用户信息">
                <!-- 子列 -->
                <el-table-column align="center" :width="nameColumnWidth" label="姓名">
                  <template slot-scope="{ row }">
                    {{ getDisplayName(row.trueName) }}
                  </template>
                </el-table-column>

                <el-table-column align="center" :width="genderColumnWidth" label="性别">
                  <template slot-scope="{ row }">
                    {{ formatGender(row.sex) }}
                  </template>
                </el-table-column>

                <el-table-column align="center" :width="ageColumnWidth" label="年龄">
                  <template slot-scope="{ row }">
                    {{ formatAge(row.age, row.birthday) }}
                  </template>
                </el-table-column>
              </el-table-column>

              <!-- 动态量表列 -->
              <template v-for="assessment in selectedAssessments">
                <!-- 量表组标题列 - 跨3列显示 -->
                <el-table-column 
                  align="center" 
                  :width="assessmentGroupWidth" 
                  :min-width="assessmentGroupWidth"
                  :key="'group-' + assessment.id"
                  :label="assessment.assessmentName"
                >
                  <!-- 子列 -->
                  <el-table-column align="center" :width="assessmentTimeWidth" :min-width="assessmentTimeWidth" :key="'time-' + assessment.id" label="测评时间">
                    <template slot-scope="{ row }">
                      {{ getAssessmentTime(row, assessment.id) }}
                    </template>
                  </el-table-column>

                  <el-table-column align="center" :width="assessmentScoreWidth" :min-width="assessmentScoreWidth" :key="'score-' + assessment.id" label="得分">
                    <template slot-scope="{ row }">
                      {{ getAssessmentScore(row, assessment.id) }}
                    </template>
                  </el-table-column>

                  <el-table-column align="center" :width="assessmentResultWidth" :min-width="assessmentResultWidth" :key="'result-' + assessment.id" label="结果">
                    <template slot-scope="{ row }">
                      {{ getAssessmentResult(row, assessment.id) }}
                    </template>
                  </el-table-column>
                </el-table-column>
              </template>

              <!-- 睡眠报告组 (条件显示) -->
              <template v-if="filterData.sleepReportShow === 1">
                <el-table-column align="center" :width="sleepReportGroupWidth" :min-width="sleepReportGroupWidth" label="睡眠报告">
                  <!-- 子列 -->
                  <el-table-column align="center" :width="sleepReportTimeWidth" label="报告生成时间">
                    <template slot-scope="{ row }">
                      {{ placeholderText(row.sleepReportTime) }}
                    </template>
                  </el-table-column>

                  <el-table-column align="center" :width="sleepReportAhiWidth" label="AHI/OAHI">
                    <template slot-scope="{ row }">
                      {{ placeholderText(row.ahi) }}
                    </template>
                  </el-table-column>
                </el-table-column>
              </template>

              <!-- 睡眠初筛报告组 (条件显示) -->
              <template v-if="filterData.ringReportShow === 1">
                <el-table-column align="center" :width="ringReportGroupWidth" :min-width="ringReportGroupWidth" label="睡眠初筛报告">
                  <!-- 子列 -->
                  <el-table-column align="center" :width="ringReportTimeWidth" label="报告生成时间">
                    <template slot-scope="{ row }">
                      {{ placeholderText(row.ringReportTime) }}
                    </template>
                  </el-table-column>

                  <el-table-column align="center" :width="ringReportIndexWidth" label="氧减指数">
                    <template slot-scope="{ row }">
                      {{ placeholderText(row.downIndex) }}
                    </template>
                  </el-table-column>
                </el-table-column>
              </template>
            </el-table>
          </page-list-container>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ResearchFilterBar from "@c/research-center/research-filter-bar.vue";
import PageListContainer from "@c/operation-log/common/paging-list/page-list-container.vue";
import { DateTool } from "@js/date-tool.js";
import { downloadExportFile } from "@js/report-export-tool.js";
import dayjs from "dayjs";
import { getDisplayName } from "@js/anonymization-tool.js";

export default {
  components: {
    ResearchFilterBar,
    PageListContainer,
  },

  data() {
    return {
      filterData: {
        startTime: undefined,
        endTime: undefined,
        type: [],
        sleepReportShow: 1,
        ringReportShow: 1,
        keyword: ""
      },
      assessmentList: [],
      selectedAssessmentList: [],
      tableData: [],
      total: 0,
      pageSize: 10,
      currentPage: 1,
    };
  },

  computed: {
    hospitalID() {
      return this.$store.state.selectedHospital.hospitalid;
    },

    selectedAssessments() {
      if (!this.filterData.type || this.filterData.type.length === 0) {
        return [];
      }
      const assessmentList = this.hospitalID
        ? this.assessmentList
        : this.selectedAssessmentList;
      return assessmentList.filter(assessment =>
        this.filterData.type.includes(assessment.id)
      );
    },

    assessmentType() {
      const type = [4, 5, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 24, 34, 35, 36, 44, 45];
      if (this.hospitalID === 1613 || this.hospitalID === 1356) { // 河南中医药大学第三附属医院, 烟台市中医院脑病科 定制
        type.push(29, 30, 31, 32, 33);
      } else if(this.hospitalID === 1853) { // 河南省儿童睡眠呼吸疾病早期筛查与精准诊疗工程研究中心 定制
        type.push(46);
      }
      return type;
    },

    // 强制表格重新渲染的key，确保列变化时数据正确显示
    tableKey() {
      return `${this.selectedAssessments.map(a => a.id).join('_')}_${this.tableData.length}`;
    },

    // 用户信息组动态宽度
    userInfoGroupWidth() {
      return this.selectedAssessments.length > 2 ? 350 : 500;
    },
    nameColumnWidth() {
      return this.selectedAssessments.length > 2 ? 150 : 200;
    },
    genderColumnWidth() {
      return this.selectedAssessments.length > 2 ? 100 : 150;
    },
    ageColumnWidth() {
      return this.selectedAssessments.length > 2 ? 100 : 150;
    },

    // 量表组动态宽度
    assessmentGroupWidth() {
      return this.selectedAssessments.length > 2 ? 350 : 410;
    },
    assessmentTimeWidth() {
      return this.selectedAssessments.length > 2 ? 140 : 160;
    },
    assessmentScoreWidth() {
      return this.selectedAssessments.length > 2 ? 100 : 120;
    },
    assessmentResultWidth() {
      return this.selectedAssessments.length > 2 ? 110 : 130;
    },

    // 睡眠报告组动态宽度
    sleepReportGroupWidth() {
      return this.selectedAssessments.length > 2 ? 400 : 550;
    },
    sleepReportTimeWidth() {
      return this.selectedAssessments.length > 2 ? 250 : 350;
    },
    sleepReportAhiWidth() {
      return this.selectedAssessments.length > 2 ? 150 : 200;
    },

    // 睡眠初筛报告组动态宽度
    ringReportGroupWidth() {
      return this.selectedAssessments.length > 2 ? 400 : 550;
    },
    ringReportTimeWidth() {
      return this.selectedAssessments.length > 2 ? 250 : 350;
    },
    ringReportIndexWidth() {
      return this.selectedAssessments.length > 2 ? 150 : 200;
    }
  },

  async mounted() {
    // 加载量表数据（需要hospitalId参数）
    await this.loadAssessmentList();
    this.loadDataList();
  },

  watch: {
    hospitalID() {
      // 重新加载量表列表（因为不同医院可能有不同的量表）
      this.loadAssessmentList();

      this.currentPage = 1;
      this.loadDataList();
    }
  },

  methods: {
    async loadAssessmentList() {
      if (!this.hospitalID) {
        this.selectedAssessmentList = this.assessmentList.filter(assessment =>
          this.filterData.type.includes(assessment.id)
        );
        this.assessmentList = [];
        return;
      }

      try {
        console.log('开始加载量表列表...');
        const params = {
          hospitalId: this.hospitalID,
          type: this.assessmentType
        };
        console.log('量表列表请求参数:', params);
        const response = await this.$api.assessemntList(params);
        console.log('量表列表API响应:', response);
        // 处理响应数据格式，转换为下拉框需要的格式
        this.assessmentList = (response || []).map(assessment => ({
          id: assessment.type,
          assessmentName: assessment.title
        }));
        this.selectedAssessmentList = [];
        console.log('设置量表列表数据:', this.assessmentList);
      } catch (error) {
        console.error('加载量表列表失败:', error);
        this.$toast.showRed(`加载量表列表失败: ${error}`);
      }
    },

    async loadDataList() {
      try {
        const params = {
          endTime: this.filterData.endTime,
          hospitalId: this.hospitalID,
          keyword: this.filterData.keyword,
          pageNum: this.currentPage,
          pageSize: this.pageSize,
          ringReportShow: this.filterData.ringReportShow,
          sleepReportShow: this.filterData.sleepReportShow,
          startTime: this.filterData.startTime,
          type: this.filterData.type.length > 0 ? this.filterData.type : undefined
        };

        Object.keys(params).forEach(key => {
          if (params[key] === undefined || params[key] === null || params[key] === '' ||
              (Array.isArray(params[key]) && params[key].length === 0)) {
            delete params[key];
          }
        });

        const response = await this.$api.getResearchCenterList(params);
        // 为每行数据添加唯一标识，防止表格数据错乱
        this.tableData = (response.list || []).map((item, index) => ({
          ...item,
          uniqueRowKey: item.userId || item.id || `row_${index}_${Date.now()}`
        }));
        this.total = response.total || 0;
      } catch (error) {
        this.$toast.showRed(error);
      }
    },

    onSearchClick() {
      this.currentPage = 1;
      this.loadDataList();
    },

    onFilterChange() {
      // 独立筛选条件变化时立即触发查询，重置页码到第一页
      this.currentPage = 1;
      this.loadDataList();
    },

    async onExportClick() {
      if (this.total === 0) {
        this.$toast.showRed("暂无数据");
        return;
      }
      
      try {
        // 构建导出参数（与查询参数相同，但不需要分页参数）
        const params = {
          endTime: this.filterData.endTime,
          hospitalId: this.hospitalID,
          keyword: this.filterData.keyword,
          ringReportShow: this.filterData.ringReportShow,
          sleepReportShow: this.filterData.sleepReportShow,
          startTime: this.filterData.startTime,
          type: this.filterData.type.length > 0 ? this.filterData.type : undefined
        };

        // 过滤掉 undefined 的参数
        Object.keys(params).forEach(key => {
          if (params[key] === undefined || params[key] === null || params[key] === '' ||
              (Array.isArray(params[key]) && params[key].length === 0)) {
            delete params[key];
          }
        });

        console.log('导出参数:', params);
        const response = await this.$api.getResearchCenterExport(params);
        
        downloadExportFile(response);
        this.$toast.showGreen("导出成功");
      } catch (error) {
        console.error('导出失败:', error);
        this.$toast.showRed(`导出失败: ${error}`);
      }
    },
    onPageChange(currentPage) {
      this.currentPage = currentPage;
      this.loadDataList();
    },

    onSizeChange(size) {
      this.pageSize = size;
      this.currentPage = 1;
      this.loadDataList();
    },

    placeholderText(rawValue, unitText = "") {
      if (!rawValue || rawValue === "null" || rawValue === "" || rawValue === undefined || rawValue === null) {
        return "-";
      } else {
        const finalUnitText = unitText ? ` ${unitText}` : "";
        return `${rawValue}${finalUnitText}`;
      }
    },

    getDisplayName(value) {
      return getDisplayName(value, this.$store.state.accountInfo);
    },

    formatGender(sex) {
      switch (sex) {
        case 1:
          return "男";
        case 2:
          return "女";
        default:
          return this.placeholderText("");
      }
    },

    formatAge(age, birthday) {
      // 优先使用 age 字段 (但排除0值，因为0可能是无效数据)
      if (age !== null && age !== undefined && age !== "null" && age !== "" && age !== 0) {
        // 检查age是否已经包含单位（如"三月龄"、"1岁"等）
        if (typeof age === 'string' && (age.includes('岁') || age.includes('月') || age.includes('天') || age.includes('龄'))) {
          return age; // 已有单位，直接返回
        }
        return `${age}岁`; // 没有单位，添加"岁"
      }
      
      // 如果没有 age，尝试从 birthday 计算
      if (birthday) {
        // 处理只有年份的情况（如 "1963"）
        if (/^\d{4}$/.test(birthday.toString())) {
          const birthYear = parseInt(birthday);
          const currentYear = new Date().getFullYear();
          return `${currentYear - birthYear}岁`;
        }
        
        // 处理完整日期格式
        const birthDay = dayjs(birthday);
        if (birthDay.isValid()) {
          const now = dayjs();
          const years = now.diff(birthDay, 'year');
          
          if (years < 1) {
            // 不到1岁，显示月龄
            const months = now.diff(birthDay, 'month');
            return months > 0 ? `${months}月龄` : "新生儿";
          } else {
            // 1岁以上，显示岁数
            return `${years}岁`;
          }
        }
      }
      
      return "-";
    },

    getAssessmentTime(row, assessmentId) {
      const assessment = row.assessmentResultList?.find(a => a.type === assessmentId);
      return assessment ? assessment.gmtCreate : "-";
    },

    getAssessmentScore(row, assessmentId) {
      console.log('row',row, assessmentId);
      const assessment = row.assessmentResultList?.find(a => a.type === assessmentId);
      if (assessment && assessment.total !== null && assessment.total !== undefined && assessment.total !== "" && assessment.total !== "null") {
        return `${assessment.total}分`;
      }
      return "-";
    },

    getAssessmentResult(row, assessmentId) {
      const assessment = row.assessmentResultList?.find(a => a.type === assessmentId);
      return assessment ? this.placeholderText(assessment.result) : "-";
    }
  },
};
</script>

<style lang="scss" scoped>
.research-center {
  background-color: #eff2f7;

  &-content {
    background-color: white;
    box-sizing: border-box;
    padding: 0 20px;
    flex: 1;
  }
}

.content {
  &-main {
    background-color: white;
    border-radius: rem(2);
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;

    &-table {
      width: 100%;
      height: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      
      &.has-horizontal-scroll {
        overflow-x: auto;
        
        // 美化滚动条
        &::-webkit-scrollbar {
          height: 8px;
        }
        
        &::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }
        
        &::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 4px;
          
          &:hover {
            background: #a8a8a8;
          }
        }
        
        :deep(.el-table) {
          min-width: max-content;
        }
        
        // 确保表头和表体可以横向滚动
        :deep(.el-table__header-wrapper),
        :deep(.el-table__body-wrapper) {
          overflow-x: auto;
        }
        
        // 确保固定列不受滚动影响
        :deep(.el-table__fixed-left) {
          position: sticky;
          left: 0;
          z-index: 10;
        }
      }
    }
  }
}

// 修复固定列嵌套表头重复显示问题
:deep(.el-table__fixed-left) {
  // 只隐藏用户信息组（前3列）的重复子列标题
  .el-table__header tr:nth-child(2) {
    th:nth-child(1),  // 姓名列
    th:nth-child(2),  // 性别列  
    th:nth-child(3) {  // 年龄列
      display: none;
    }
  }
}

:deep(.el-table) {
  font-size: rem(14);
  
  // 所有表头的基础样式
  .el-table__header-wrapper th,
  .el-table__fixed .el-table__header-wrapper th,
  .el-table__fixed-right .el-table__header-wrapper th,
  .el-table__fixed-left .el-table__header-wrapper th {
    // background-color: #fafafa !important;
    color: rgba(0, 0, 0, 0.85) !important;
    font-size: rem(14) !important;
    font-weight: normal !important;
    border-bottom: rem(1) solid #e8e8e8 !important;
    height: 80px !important;
    padding: 8px !important;
    text-align: center !important;
  }
 
  
  // 专门为固定列的表头设置样式（排除前三列）
  .el-table__fixed-left-patch,
  .el-table__fixed-left .el-table__header th:nth-child(n+4),
  .el-table__fixed-left .el-table__header-wrapper .el-table__header th:nth-child(n+4) {
    background-color: #fafafa !important;
  }
  

  
  // 固定列补丁区域
  .el-table__fixed-right-patch,
  .el-table__fixed-left-patch {
    background-color: #fafafa !important;
    height: 80px !important;
  }
  
  // 固定列表头
  .el-table__fixed-right .el-table__header th,
  .el-table__fixed-left .el-table__header th {
    background-color: #fafafa !important;
    height: 80px !important;
  }

  
  .el-table__fixed-right .el-table__header,
  .el-table__fixed-left .el-table__header {
    height: 80px !important;
  }
  
  .el-table__body-wrapper td,
  .el-table__fixed .el-table__body-wrapper td,
  .el-table__fixed-right .el-table__body-wrapper td,
  .el-table__fixed-left .el-table__body-wrapper td {
    color: #666666 !important;
    font-size: rem(14) !important;
    border-bottom: rem(1) solid #e8e8e8 !important;
    height: 54px !important;
    padding: 0 !important;
    text-align: center !important;
    line-height: rem(22) !important;
  }
  
  .el-table__body tr:hover td,
  .el-table__fixed .el-table__body tr:hover td,
  .el-table__fixed-right .el-table__body tr:hover td,
  .el-table__fixed-left .el-table__body tr:hover td {
    background-color: #f7f7f7 !important;
  }
  
  &.el-table--border {
    border: rem(1) solid #e8e8e8;
    
    &::after {
      display: none;
    }
    
    &::before {
      display: none;
    }
  }
  
  .el-table__fixed,
  .el-table__fixed-left {
    border-right: rem(1) solid #e8e8e8;
  }
  
  .el-table__fixed-right {
    border-left: rem(1) solid #e8e8e8;
  }
}
</style>
